import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Coins, 
  Zap, 
  Star, 
  Loader2,
  Check,
  Sparkles
} from 'lucide-react';

const BuyCreditsModal = ({ isOpen, onClose, onPurchaseComplete }) => {
  const { api, refreshUser } = useAuth();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/credit-packages');
      setPackages(response.data.packages || {});
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchaseLoading(packageId);
    try {
      const response = await api.post('/credits/purchase', {
        package_id: packageId,
        origin_url: window.location.origin
      });

      if (response.data?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Unable to start checkout');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start purchase';
      toast.error(message);
    } finally {
      setPurchaseLoading(null);
    }
  };

  const starterPacks = Object.values(packages).filter(p => p.category === 'starter');
  const powerPacks = Object.values(packages).filter(p => p.category === 'power');

  const PackageCard = ({ pkg, index }) => {
    const isHighlighted = pkg.highlight;
    const isLoading = purchaseLoading === pkg.id;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`relative rounded-xl border p-4 transition-all hover:shadow-lg ${
          isHighlighted 
            ? 'border-[#4F7CFF] bg-[#4F7CFF]/5 ring-2 ring-[#4F7CFF]/20' 
            : 'border-border hover:border-[#4F7CFF]/50'
        }`}
      >
        {pkg.badge && (
          <div className={`absolute -top-2.5 left-4 px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${
            pkg.badge === 'Best Value' 
              ? 'bg-[#4F7CFF] text-white' 
              : 'bg-green-500/10 text-green-600'
          }`}>
            {pkg.badge}
          </div>
        )}
        
        {/* Card content - stacked layout for better fit */}
        <div className="space-y-3">
          {/* Icon and name */}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isHighlighted ? 'bg-[#4F7CFF]/10' : 'bg-accent'
            }`}>
              {isHighlighted ? (
                <Sparkles className="w-4 h-4 text-[#4F7CFF]" />
              ) : (
                <Coins className="w-4 h-4" />
              )}
            </div>
            <p className="font-semibold text-sm truncate">{pkg.name}</p>
          </div>
          
          {/* Credits and price - side by side with overflow handling */}
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-2xl font-bold truncate">{pkg.credits.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">credits</p>
            </div>
            <div className="text-right flex-shrink-0">
              {pkg.original_price && (
                <p className="text-xs text-muted-foreground line-through">
                  ${pkg.original_price}
                </p>
              )}
              <p className="text-lg font-bold whitespace-nowrap">{pkg.price_display}</p>
              <p className="text-xs text-muted-foreground whitespace-nowrap">
                ${(pkg.price / pkg.credits).toFixed(3)}/cr
              </p>
            </div>
          </div>
        </div>
        
        <Button 
          className={`w-full mt-3 ${isHighlighted ? 'bg-[#4F7CFF] hover:bg-[#4F7CFF]/90' : ''}`}
          variant={isHighlighted ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePurchase(pkg.id)}
          disabled={!!purchaseLoading}
          data-testid={`buy-${pkg.id}-btn`}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Buy Now
            </>
          )}
        </Button>
      </motion.div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Coins className="w-5 h-5 text-[#4F7CFF]" />
            Buy Credits
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Starter Packs */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" />
                Starter Packs
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {starterPacks.map((pkg, index) => (
                  <PackageCard key={pkg.id} pkg={pkg} index={index} />
                ))}
              </div>
            </div>

            {/* Power Packs */}
            {powerPacks.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Power Packs
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {powerPacks.map((pkg, index) => (
                    <PackageCard key={pkg.id} pkg={pkg} index={index + starterPacks.length} />
                  ))}
                </div>
              </div>
            )}

            {/* Info Text */}
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">
                Credits are added instantly to your account. One-time purchase, no subscription required.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BuyCreditsModal;
